import { BACKDROPS, GLYPHS, PALETTE } from "@eldrun/content";
import { h } from "../kit/dom.ts";
import { ISLAND_IMAGE_ORIGIN, ISLAND_IMAGE_SIZE, type IslandLayers } from "../panels/island-map.ts";
import type { FogOfWar, MapCircle, MapMarker } from "../panels/map-model.ts";
import { MINIMAP_RANGE, minimapMarkers } from "./minimap-model.ts";

/** Linhas da grade e dos anéis tracejados (o neutro apagado, como no mockup). */
const LINE = "rgba(232, 238, 242, 0.16)";
const SHADOW = "rgba(0, 0, 0, 0.85)";

/**
 * Minimapa redondo (F81, mockup ui-b, em cima à direita): a ilha e a névoa do mapa (`IslandLayers`, as
 * mesmas do M), a grade e o anel tracejado, o Cerco e os marcadores do mapa (aliados numerados, a coroa do
 * chefe, pings na cor de quem pingou, lugares descobertos), num canvas redesenhado a 10 Hz pela cena. A
 * seta do centro é DOM e gira por quadro com a câmera. A moldura de cristal fica por cima (CSS).
 */
export class Minimap {
  readonly element: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly arrow: HTMLElement;
  private readonly layers: IslandLayers;
  private heading = Number.NaN;

  constructor(parent: HTMLElement, layers: IslandLayers) {
    this.layers = layers;
    this.canvas = h("canvas", { class: "minimapa-tela", "aria-hidden": "true" });
    this.arrow = h("span", { class: "minimapa-eu", "aria-hidden": "true" });
    this.element = h(
      "div",
      { id: "minimapa", role: "img", "aria-label": "Minimapa" },
      this.canvas,
      this.arrow,
      h("span", { class: "minimapa-moldura", "aria-hidden": "true" }),
      h("span", { class: "minimapa-norte", "aria-hidden": "true" }, "N"),
    );
    parent.appendChild(this.element);
  }

  /** Por quadro: a seta gira com o rumo da câmera (rad, horário a partir do norte). */
  turn(heading: number): void {
    const rounded = Math.round(heading * 200) / 200;
    if (rounded === this.heading) return;
    this.heading = rounded;
    this.arrow.style.transform = `translate(-50%, -50%) rotate(${rounded.toFixed(3)}rad)`;
  }

  /** Redesenha centrado em (cx, cz): a ilha, a névoa, o Cerco e os marcadores. Chamado a ≤ 10 Hz. */
  draw(cx: number, cz: number, fog: FogOfWar, markers: readonly MapMarker[], circles: readonly MapCircle[]): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const size = Math.max(1, Math.round(this.canvas.clientWidth));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (this.canvas.width !== Math.round(size * dpr)) this.canvas.width = this.canvas.height = Math.round(size * dpr);
    this.layers.refresh(fog);
    const r = size / 2;
    const s = r / MINIMAP_RANGE;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = BACKDROPS.fundo;
    ctx.fillRect(0, 0, size, size);
    // A ilha e a névoa, como no mapa (suavizadas: no minimapa a célula de 8 m tem poucos pixels).
    ctx.imageSmoothingEnabled = true;
    const x0 = r + (ISLAND_IMAGE_ORIGIN - cx) * s;
    const z0 = r + (ISLAND_IMAGE_ORIGIN - cz) * s;
    const side = ISLAND_IMAGE_SIZE * s;
    ctx.globalAlpha = 0.55;
    ctx.drawImage(this.layers.base, x0, z0, side, side);
    ctx.globalAlpha = 1;
    ctx.drawImage(this.layers.fog, x0, z0, side, side);
    // A grade (presa ao mundo, de 40 em 40 m), a cruz tracejada e o anel do meio, como no mockup.
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 1;
    const step = 40 * s;
    const gx = (r - cx * s) % step;
    const gz = (r - cz * s) % step;
    ctx.beginPath();
    for (let x = gx; x < size; x += step) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, size);
    }
    for (let z = gz; z < size; z += step) {
      ctx.moveTo(0, z);
      ctx.lineTo(size, z);
    }
    ctx.stroke();
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(r, size);
    ctx.moveTo(0, r);
    ctx.lineTo(size, r);
    ctx.moveTo(r + r * 0.62, r);
    ctx.arc(r, r, r * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    for (const c of circles) {
      ctx.beginPath();
      ctx.arc(r + (c.x - cx) * s, r + (c.z - cz) * s, c.r * s, 0, Math.PI * 2);
      ctx.lineWidth = c.kind === "cerco" ? 2 : 1.5;
      ctx.setLineDash(c.kind === "cerco" ? [] : [6, 4]);
      ctx.strokeStyle = PALETTE.neutro.hex;
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = 3;
    ctx.strokeStyle = SHADOW;
    const font = Math.max(10, Math.round(size / 16));
    for (const m of minimapMarkers(markers, cx, cz, r)) {
      const x = r + m.x;
      const y = r + m.y;
      const mk = m.marker;
      ctx.globalAlpha = m.pinned ? 0.8 : 1;
      if (mk.kind === "aliado" && /^\d$/.test(mk.symbol)) {
        // Aliado: o hexágono na cor do slot com o número dentro (como no mapa).
        ctx.font = `${font + 6}px system-ui, sans-serif`;
        ctx.strokeText(GLYPHS.hexagono.char, x, y);
        ctx.fillStyle = mk.color;
        ctx.fillText(GLYPHS.hexagono.char, x, y);
        ctx.font = `bold ${font - 2}px system-ui, sans-serif`;
        ctx.fillStyle = BACKDROPS.fundo;
        ctx.fillText(mk.symbol, x, y + 1);
      } else {
        const major = mk.kind === "chefe" || mk.kind === "ping" || mk.kind === "aliado";
        ctx.font = `${major ? "bold " : ""}${major ? font + 2 : font - 1}px system-ui, sans-serif`;
        ctx.strokeText(mk.symbol, x, y);
        ctx.fillStyle = mk.color;
        ctx.fillText(mk.symbol, x, y);
      }
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  remove(): void {
    this.element.remove();
  }
}
